<template>
  <BittsLayout class="c-data-sharing-page" :is-page="true">
    <template #content>
      <PageTitle
        title="Default Sharing"
        subtitle="These sharing settings apply to new partners."
        class="mb-40"
      >
        <template #action>
          <BittsButton
            v-if="hasSharingPermission && hasManagePops"
            text="Save"
            :disabled="!sharingSettingsTabRef?.hasChanges || isLoading"
            @click="handleSave"
          />
        </template>
      </PageTitle>
      <SharingSettingsTab ref="sharingSettingsTabRef" class="m-0" />
    </template>
    <template #populations-modal>
      <router-view-wrapper />
    </template>
  </BittsLayout>
  <BittsModalTwo
    v-if="showUnsavedChangesModal"
    :width="500"
    variant="confirm"
    title="Unsaved changes"
    primary-button-text="Save Changes"
    secondary-button-text="Discard Changes"
    description="You have unsaved changes. Would you like to save them before leaving?"
    @closed="goNext"
    @action="saveAndGoNext"
  />
</template>

<script setup lang="ts">
import { BittsButton, BittsLayout, BittsModalTwo } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';
import {
  RouteLocationNormalized,
  onBeforeRouteLeave,
  useRouter,
} from 'vue-router';

import PageTitle from '@/components/PageTitle.vue';
import { SharingSettingsTab } from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab';

import useAuth from '@/composables/useAuth';

useHead({
  title: 'Default Sharing - Crossbeam',
});

const router = useRouter();

const sharingSettingsTabRef = ref<InstanceType<
  typeof SharingSettingsTab
> | null>(null);

const nextRoute = ref<RouteLocationNormalized | null>(null);
const showUnsavedChangesModal = ref(false);
const isLoading = ref(false);

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));
const hasManagePops = computed(() => hasPermission('manage:populations'));

async function handleSave() {
  isLoading.value = true;
  await sharingSettingsTabRef.value?.triggerBulkUpdate();
  isLoading.value = false;
}

async function goNext() {
  if (nextRoute.value) {
    await router.push({ ...nextRoute.value });
  }
}

async function saveAndGoNext() {
  await handleSave();
  await goNext();
}

onBeforeRouteLeave((to, _from, next) => {
  if (
    !showUnsavedChangesModal.value &&
    sharingSettingsTabRef.value?.hasChanges
  ) {
    nextRoute.value = to;
    showUnsavedChangesModal.value = true;
    next(false);
  } else {
    next();
  }
});
</script>
